import { SxProps } from "@outschool/backpack";

/** Maximum display length for learner names (12 chars + 3 for ellipsis) */
export const MAX_LEARNER_NAME_DISPLAY_LENGTH = 18;
const ELLIPSIS = "...";

/**
 * Truncates a learner name to fit within MAX_LEARNER_NAME_DISPLAY_LENGTH.
 * Names longer than the limit will be truncated with an ellipsis.
 */
export function truncateLearnerName(name: string): string {
  if (name.length <= MAX_LEARNER_NAME_DISPLAY_LENGTH) {
    return name;
  }
  const truncatedLength = MAX_LEARNER_NAME_DISPLAY_LENGTH - ELLIPSIS.length;
  return name.slice(0, truncatedLength) + ELLIPSIS;
}

export const LearnerSelectorVariants = {
  TOP_BAR: "top-bar",
  LISTING_FILTERS: "listing-filters",
} as const;

export type LearnerSelectorVariant =
  (typeof LearnerSelectorVariants)[keyof typeof LearnerSelectorVariants];

export interface VariantConfig {
  buttonVariant: "text" | "contained";
  buttonSx: SxProps;
  showAgeInButton: boolean;
}

export const VARIANT_CONFIGS: Record<LearnerSelectorVariant, VariantConfig> = {
  [LearnerSelectorVariants.TOP_BAR]: {
    buttonVariant: "text",
    buttonSx: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.4em",
      px: 14,
      height: "100%",
      border: "none",
      boxShadow: "none",
      marginLeft: "2px",

      //   Custom text button variant for this use-case.
      color: "primary.900",
      backgroundColor: "primary.100",
      "&:disabled": {
        backgroundColor: "transparent",
      },
      "&:hover:not(:disabled), &:focus:not(:disabled)": {
        backgroundColor: "primary.200",
      },
      "&:active:not(:disabled)": {
        backgroundColor: "primary.300",
      },
    },
    showAgeInButton: false,
  },
  [LearnerSelectorVariants.LISTING_FILTERS]: {
    buttonVariant: "contained",
    buttonSx: {
      paddingTop: "7px",
      paddingBottom: "7px",
      paddingLeft: "12px",
      paddingRight: "12px",
      gap: "0.4em",
      fontSize: "14px",
      lineHeight: 1,
      borderWidth: "1px",
      "&:hover:not(:disabled), &:focus:not(:disabled), &:active:not(:disabled)":
        {
          borderWidth: "1px",
        },
    },
    showAgeInButton: true,
  },
};

export interface LearnerSelectorProps {
  /** Visual variant controlling button styling and behavior. */
  variant: LearnerSelectorVariant;

  /** Tracking location for analytics (e.g., "global_search_bar", "desktop_search") */
  trackingLocation: string;

  /** Label for the "all learners" option. Defaults to "All learners". */
  allLabel?: string;

  /** External open state. If provided, the component uses this instead of internal state. */
  isOpen?: boolean;

  /** Callback when the dropdown should open. */
  onOpen?: () => void;

  /** Callback when the dropdown should close. */
  onClose?: () => void;
}
